import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AppProvider, useAppContext } from "./context/AppContext";
import { WelcomeScreen } from "./screens/WelcomeScreen";
import { ManagerView } from "./screens/ManagerView";
import { OperatorView } from "./screens/OperatorView";
import { MultiCameraOperatorView } from "./screens/MultiCameraOperatorView";
import type { JSX } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "./services/ApiService";
import { useRoleContext } from "./hooks/useRoleContext";

// Protected route for Manager
const ProtectedManagerRoute = ({ children }: { children: JSX.Element }) => {
  const { isManagerAuthenticated } = useAppContext();
  
  // Set role context for manager routes
  const { hasValidTokens } = useRoleContext();

  if (!isManagerAuthenticated || !hasValidTokens) {
    return <Navigate to="/" />;
  }

  return children;
};

// Protected route for Operator
const ProtectedOperatorRoute = ({ children }: { children: JSX.Element }) => {
  const { operatorName } = useAppContext();
  
  // Set role context for verificator routes
  const { hasValidTokens } = useRoleContext();

  if (!operatorName || !hasValidTokens) {
    return <Navigate to="/" />;
  }

  return children;
};

const AuthListener = () => {
  const navigate = useNavigate();
  const { setIsManagerAuthenticated } = useAppContext();

  useEffect(() => {
    const onLogout = (event: CustomEvent) => {
      const role = event.detail?.role;
      
      // Clear API tokens/cache/queue for the specific role
      if (role) {
        apiService.logoutForRole(role);
      } else {
        apiService.logout();
      }
      
      // Only update manager auth state if the logout is for manager role
      if (role === "manager") {
        setIsManagerAuthenticated(false);
      }
      
      // Redirect to welcome
      navigate("/", { replace: true });
    };

    window.addEventListener("auth:logout", onLogout as EventListener);
    return () => {
      window.removeEventListener("auth:logout", onLogout as EventListener);
    };
  }, [navigate, setIsManagerAuthenticated]);

  return null;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<WelcomeScreen />} />
      <Route
        path="/manager"
        element={
          <ProtectedManagerRoute>
            <ManagerView />
          </ProtectedManagerRoute>
        }
      />
      <Route
        path="/operator"
        element={
          <ProtectedOperatorRoute>
            <OperatorView />
          </ProtectedOperatorRoute>
        }
      />
      <Route
        path="/multi-camera"
        element={
          <ProtectedOperatorRoute>
            <MultiCameraOperatorView />
          </ProtectedOperatorRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AppProvider>
      <Router>
        <AuthListener />
        <AppRoutes />
      </Router>
    </AppProvider>
  );
}

export default App;
